import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function ProtectedRoute({ children, founderOnly = false }) {
  const { user, loading, sessionError, refreshing, refresh } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-[#666] text-[14px]">Loading…</div>
      </div>
    );
  }
  if (!user && sessionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-6">
        <div className="max-w-md space-y-3">
          <p role="alert" className="text-sm text-slate-700">{sessionError}</p>
          <button type="button" disabled={refreshing} onClick={() => refresh()}
            className="rounded-lg border px-4 py-2 text-sm disabled:opacity-50">
            {refreshing ? "Checking session…" : "Try again"}
          </button>
        </div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  // Kickstart-intent redirect: if the user clicked a Kickstart CTA
  // before authenticating, the tier key was stashed in localStorage.
  // Once they land on /dashboard or /onboarding, route them straight
  // to /subscribe/kickstart_X so they never see a "you're on trial"
  // detour when their real intent was to purchase Kickstart. The key
  // is cleared by SubscribeTier on mount so this fires exactly once.
  try {
    const intent = localStorage.getItem("zynthoro_kickstart_intent");
    if (intent && /^kickstart_[123]$/.test(intent)) {
      if (location.pathname === "/dashboard" || location.pathname === "/onboarding") {
        return <Navigate to={`/subscribe/${intent}`} replace />;
      }
    }
  } catch { /* ignore */ }
  // Fictional demos never enter real company onboarding or paid modules.
  if (user.business_demo && user.is_trial && !["/demo", "/pricing"].includes(location.pathname) && !location.pathname.startsWith("/subscribe/")) {
    return <Navigate to="/demo" replace />;
  }
  // Presale users skip onboarding entirely — straight to dashboard.
  const ownWorkspace = !user.active_workspace || user.active_workspace === user.id;
  const isPresale = (user.subscription_plan || "").toLowerCase() === "presale";
  // Force onboarding only when not yet completed AND user is on a paid plan.
  if (location.pathname !== "/demo" && ownWorkspace && !user.onboarding_completed && !isPresale && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }
  // Already onboarded? Don't show /onboarding again on direct navigation.
  if ((!ownWorkspace || user.onboarding_completed || isPresale) && location.pathname === "/onboarding") {
    return <Navigate to="/dashboard" replace />;
  }
  if (founderOnly && !user.is_founder) {
    return <Navigate to="/dashboard" replace />;
  }
  return (
    <>
      {sessionError && <div role="alert" className="flex flex-wrap items-center gap-3 border-b bg-amber-50 px-4 py-2 text-sm text-slate-700">
        <span>{sessionError}</span>
        <button type="button" disabled={refreshing} onClick={() => refresh()}
          className="rounded border px-3 py-1 disabled:opacity-50">
          {refreshing ? "Checking session…" : "Try again"}
        </button>
      </div>}
      {children}
    </>
  );
}

